import { useEffect, useRef } from "react";

interface Props {
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

export function DeleteModal({ title, onCancel, onConfirm, busy }: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
        return;
      }
      // Keep Tab cycling inside the dialog.
      if (e.key === "Tab" && modalRef.current) {
        const focusables = modalRef.current.querySelectorAll<HTMLElement>("button:not([disabled])");
        if (focusables.length === 0) return;
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previous?.focus();
    };
  }, [onCancel]);

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title" ref={modalRef}>
        <h3 id="delete-modal-title">Delete transcription?</h3>
        <p>"{title}" and its audio file will be removed. This cannot be undone.</p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel} type="button" disabled={busy} ref={cancelRef}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm} type="button" disabled={busy}>
            {busy ? "Deleting…" : "Delete transcription"}
          </button>
        </div>
      </div>
    </div>
  );
}
